import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type {
  APIPersonalAutoApplicationForm,
  APIRemark,
  APIRemarkFile,
  APIApplicationPayment,
  APIBinderPDF,
  APISigning,
  APIRemarkStatus,
  APIAppeal,
  APIBaraActiveReferral,
} from 'types/api.types';

import type { AutoPersonalFormState } from './autoPersonalFormReducer.types';

const initialState: AutoPersonalFormState = {
  isDataSet: false,
  isDataSaved: false,
  uuid: null,
  data: null,
  remarks: [],
  files: [],
  missingMandatoryFields: [],
  payments: null,
  binder_pdf: null,
  signing: null,
  appeal: null,
  bara_referral: null,
  wip_status: null,
  province: 1,
};

export const autoPersonalFormSlice = createSlice({
  name: 'autoPersonalForm',
  initialState,
  reducers: {
    storeData: (
      state,
      action: PayloadAction<{
        uuid: string;
        data: APIPersonalAutoApplicationForm;
        remarks: APIRemark[];
        files: APIRemarkFile[];
        payments: APIApplicationPayment[];
        binder_pdf: APIBinderPDF | null;
        signing: APISigning | null;
        appeal: APIAppeal | null;
        bara_referral: APIBaraActiveReferral | null;
        wip_status: number | null;
        province: number;
      }>,
    ) => {
      state.uuid = action.payload.uuid;
      state.data = {
        ...action.payload.data,
        additional_insureds: action.payload.data.additional_insureds ?? [],
      };

      state.remarks = action.payload.remarks;
      state.files = action.payload.files;
      state.payments = action.payload.payments;
      state.binder_pdf = action.payload.binder_pdf;
      state.signing = action.payload.signing;
      state.appeal = action.payload.appeal;
      state.bara_referral = action.payload.bara_referral;
      state.wip_status = action.payload.wip_status;
      state.province = action.payload.province;

      state.missingMandatoryFields = [];
      state.isDataSet = true;
    },

    resetData: (state) => {
      state.isDataSet = false;
      state.uuid = null;
      state.data = null;
      state.remarks = [];
      state.files = [];
    },

    changeIsDataSaved: (state, action: PayloadAction<boolean>) => {
      if (state.signing !== null) {
        state.isDataSaved = true;
        return;
      }

      state.isDataSaved = action.payload;
    },

    changePolicyInformation: (
      state,
      action: PayloadAction<{ name: string; value: string | boolean }>,
    ) => {
      state.data.policy_information = {
        ...state.data.policy_information,
        [action.payload.name]: action.payload.value,
      };
    },

    changeLanguage: (state, action: PayloadAction<'en' | 'fe'>) => {
      state.data.policy_information = {
        ...state.data.policy_information,
        language_preferred: action.payload,
      };
    },

    changeCompanyAndBrokerInformation: (
      state,
      action: PayloadAction<{ name: string; value: string }>,
    ) => {
      state.data.company_and_broker_information = {
        ...state.data.company_and_broker_information,
        [action.payload.name]: action.payload.value,
      };
    },

    changeApplicant: (
      state,
      action: PayloadAction<{ name: string; value: string }>,
    ) => {
      state.data.applicant = {
        ...state.data.applicant,
        [action.payload.name]: action.payload.value,
      };

      // Auto-sync first account holder if it's set to same as applicant
      if (state.data.account_holders.first.is_same_as_applicant) {
        const syncFields = [
          'email',
          'first_name',
          'middle_name',
          'last_name',
          'address',
          'city',
          'province_code',
          'postal_code',
        ];
        if (syncFields.includes(action.payload.name)) {
          state.data.account_holders.first = {
            ...state.data.account_holders.first,
            [action.payload.name]: action.payload.value,
          };
        }
        // Handle phone number concatenation when phone fields change
        if (
          action.payload.name === 'phone_home_prefix' ||
          action.payload.name === 'phone_home'
        ) {
          const prefix =
            action.payload.name === 'phone_home_prefix'
              ? action.payload.value
              : state.data.applicant.phone_home_prefix;
          const number =
            action.payload.name === 'phone_home'
              ? action.payload.value
              : state.data.applicant.phone_home;
          state.data.account_holders.first.phone = `${prefix}${number}`;
        }
      }
    },

    changeCoApplicant: (
      state,
      action: PayloadAction<{ name: string; value: string }>,
    ) => {
      state.data.co_applicant = {
        ...state.data.co_applicant,
        [action.payload.name]: action.payload.value,
      };

      // Auto-sync second account holder if it's set to same as co-applicant
      if (state.data.account_holders.second.is_same_as_co_applicant) {
        const syncFields = ['email', 'first_name', 'middle_name', 'last_name'];
        if (syncFields.includes(action.payload.name)) {
          state.data.account_holders.second = {
            ...state.data.account_holders.second,
            [action.payload.name]: action.payload.value,
          };
        }
      }
    },

    changePolicyPeriod: (
      state,
      action: PayloadAction<{ name: string; value: string }>,
    ) => {
      state.data.policy_period = {
        ...state.data.policy_period,
        [action.payload.name]: action.payload.value,
      };
    },

    changeVehicle: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: string | boolean;
      }>,
    ) => {
      state.data.vehicles = state.data.vehicles.map(
        (vehicle, index: number) => {
          if (index === action.payload.index) {
            const extra = {} as any;

            // Fields for which an opposite value needs to be flipped
            const reverseKeys = {
              is_owend: 'is_leased',
              is_leased: 'is_owend',
              is_purchased_new: 'is_purchased_used',
              is_purchased_used: 'is_purchased_new',
            } as any;

            if (action.payload.name in reverseKeys) {
              extra[reverseKeys[action.payload.name]] = !action.payload.value;
            }

            return {
              ...vehicle,
              [action.payload.name]: action.payload.value,
              ...extra,
            };
          }

          return vehicle;
        },
      );
    },

    changeVehicleAccidentBenefitsQuestion: (
      state,
      action: PayloadAction<{
        index: number;
        name: 'question_1' | 'question_2' | 'question_3';
        value: boolean;
      }>,
    ) => {
      state.data.vehicles = state.data.vehicles.map(
        (vehicle, index: number) => {
          if (index === action.payload.index) {
            return {
              ...vehicle,
              accident_benefits_questions: {
                ...vehicle.accident_benefits_questions,
                [action.payload.name]: action.payload.value,
              },
            };
          }

          return vehicle;
        },
      );
    },

    changeVehicleLessor: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: string | boolean;
      }>,
    ) => {
      state.data.vehicles = state.data.vehicles.map(
        (vehicle, index: number) => {
          if (index === action.payload.index) {
            return {
              ...vehicle,
              lessor: {
                ...vehicle.lessor,
                [action.payload.name]: action.payload.value,
              },
            };
          }

          return vehicle;
        },
      );
    },

    changeVehicleLienholder: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: string | boolean;
      }>,
    ) => {
      state.data.vehicles = state.data.vehicles.map(
        (vehicle, index: number) => {
          if (index === action.payload.index) {
            return {
              ...vehicle,
              lienholder_info: {
                ...vehicle.lienholder_info,
                [action.payload.name]: action.payload.value,
              },
            };
          }

          return vehicle;
        },
      );
    },

    changeVehicleSummary: (
      state,
      action: PayloadAction<{ name: string; value: string | boolean }>,
    ) => {
      state.data.vehicles_summary = {
        ...state.data.vehicles_summary,
        [action.payload.name]: action.payload.value,
      };
    },

    changeUnlistedVehicles: (
      state,
      action: PayloadAction<{
        name: string;
        value: string | boolean;
        index: number;
      }>,
    ) => {
      state.data.unlisted_vehicles = state.data.unlisted_vehicles.map(
        (vehicle, index: number) => {
          if (index === action.payload.index) {
            return {
              ...vehicle,
              [action.payload.name]: action.payload.value,
            };
          }

          return vehicle;
        },
      );
    },

    changeDriver: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: string | boolean;
      }>,
    ) => {
      state.data.drivers = state.data.drivers.map((driver, index: number) => {
        if (index === action.payload.index) {
          return {
            ...driver,
            [action.payload.name]: action.payload.value,
          };
        }

        return driver;
      });
    },

    changeDriverVehiclePercentageUse: (
      state,
      action: PayloadAction<{
        driver_index: number;
        vehicle_id: number;
        value: string;
      }>,
    ) => {
      state.data.drivers = state.data.drivers.map((driver, index: number) => {
        if (index === action.payload.driver_index) {
          return {
            ...driver,
            vehicle_percentage_use: {
              ...driver.vehicle_percentage_use,
              [`vehicle_${action.payload.vehicle_id}`]: action.payload.value,
            },
          };
        }

        return driver;
      });
    },

    changeDriverSummary: (
      state,
      action: PayloadAction<{ name: string; value: string | boolean }>,
    ) => {
      state.data.drivers_summary = {
        ...state.data.drivers_summary,
        [action.payload.name]: action.payload.value,
      };
    },

    changeMostRecentPolicy: (
      state,
      action: PayloadAction<{ name: string; value: string }>,
    ) => {
      state.data.most_recent_policy = {
        ...state.data.most_recent_policy,
        [action.payload.name]: action.payload.value,
      };
    },

    changeUnlistedSpouse: (
      state,
      action: PayloadAction<{
        name: string;
        value: string | boolean;
        index: number;
      }>,
    ) => {
      state.data.unlisted_spouses = state.data.unlisted_spouses.map(
        (spouse, index: number) => {
          if (index === action.payload.index) {
            return {
              ...spouse,
              [action.payload.name]: action.payload.value,
            };
          }

          return spouse;
        },
      );
    },

    changeAccidentsAndClaims: (
      state,
      action: PayloadAction<{
        name: string;
        index: number;
        value: string;
      }>,
    ) => {
      state.data.accidents_and_claims = state.data.accidents_and_claims.map(
        (claim, index: number) => {
          if (index === action.payload.index) {
            return {
              ...claim,
              [action.payload.name]: action.payload.value,
            };
          }

          return claim;
        },
      );
    },

    addAccidentsAndClaims: (state, action: PayloadAction<number | null>) => {
      state.data.accidents_and_claims = [
        ...state.data.accidents_and_claims,
        {
          auto_nr: '',
          date: '',
          details: '',
          driver_nr: action.payload,
          is_ab: false,
          is_bi: false,
          is_coll_ap: false,
          is_comp_sp: false,
          is_dcpd: false,
          is_pd: false,
          is_ua: false,
          paid: '',
        },
      ];
    },

    removeAccidentsAndClaims: (state, action: PayloadAction<number>) => {
      state.data.accidents_and_claims = state.data.accidents_and_claims.filter(
        (_, index: number) => index !== action.payload,
      );
    },

    changeConvictions: (
      state,
      action: PayloadAction<{ name: string; index: number; value: string }>,
    ) => {
      state.data.convictions = state.data.convictions.map(
        (conviction, index: number) => {
          if (index === action.payload.index) {
            return {
              ...conviction,
              [action.payload.name]: action.payload.value,
            };
          }

          return conviction;
        },
      );
    },

    addConviction: (state) => {
      state.data.convictions = [
        ...state.data.convictions,
        {
          date: '',
          details: '',
          driver_nr: null,
        },
      ];
    },

    removeConviction: (state, action: PayloadAction<number>) => {
      state.data.convictions = state.data.convictions.filter(
        (_, index: number) => index !== action.payload,
      );
    },

    changeRatingInformationDrivingRecord: (
      state,
      action: PayloadAction<{ name: string; index: number; value: string }>,
    ) => {
      state.data.rating_information_driving_record =
        state.data.rating_information_driving_record.map(
          (record, index: number) => {
            if (index === action.payload.index) {
              return {
                ...record,
                [action.payload.name]: action.payload.value,
              };
            }

            return record;
          },
        );
    },

    addRatingInformationDrivingRecord: (state) => {
      state.data.rating_information_driving_record = [
        ...state.data.rating_information_driving_record,
        {
          ab: '',
          at_fault_claim_description: '',
          at_fault_claim_percentage: '',
          auto_class: '',
          auto_nr: null,
          bi: '',
          coll_ap: '',
          conviction_description: '',
          conviction_percentage: '',
          dcpd: '',
          driver_principal: '',
          driver_secondary: '',
          pd: '',
        },
      ];
    },

    removeRatingInformationDrivingRecord: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.data.rating_information_driving_record =
        state.data.rating_information_driving_record.filter(
          (_, index: number) => index !== action.payload,
        );
    },

    changeRatingInformationDiscounts: (
      state,
      action: PayloadAction<{ name: string; index: number; value: string }>,
    ) => {
      state.data.rating_information_discounts =
        state.data.rating_information_discounts.map(
          (discount, index: number) => {
            if (index === action.payload.index) {
              return {
                ...discount,
                [action.payload.name]: action.payload.value,
              };
            }

            return discount;
          },
        );
    },

    addRatingInformationDiscounts: (state) => {
      state.data.rating_information_discounts = [
        ...state.data.rating_information_discounts,
        {
          ab: '',
          auto_nr: null,
          coll_ap: '',
          comp_sp: '',
          dcpd: '',
          list_price_new: '',
          location: '',
          postal_code: '',
          territory: '',
          vehicle_code: '',
          discounts: [
            {
              discount_description: '',
              discount_percentage: '',
            },
          ],
        },
      ];
    },

    removeRatingInformationDiscount: (state, action: PayloadAction<number>) => {
      state.data.rating_information_discounts =
        state.data.rating_information_discounts.filter(
          (_, index: number) => index !== action.payload,
        );
    },

    changeRatingInformationDiscountsDiscount: (
      state,
      action: PayloadAction<{
        name: string;
        index: number;
        discountIndex: number;
        value: string;
      }>,
    ) => {
      state.data.rating_information_discounts =
        state.data.rating_information_discounts.map(
          (discount, index: number) => {
            if (index === action.payload.index) {
              return {
                ...discount,
                discounts: discount.discounts.map(
                  (discount, discountIndex: number) => {
                    if (discountIndex === action.payload.discountIndex) {
                      return {
                        ...discount,
                        [action.payload.name]: action.payload.value,
                      };
                    }

                    return discount;
                  },
                ),
              };
            }

            return discount;
          },
        );
    },

    addRatingInformationDiscountsDiscount: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.data.rating_information_discounts =
        state.data.rating_information_discounts.map(
          (discount, index: number) => {
            if (index === action.payload) {
              return {
                ...discount,
                discounts: [
                  ...discount.discounts,
                  {
                    discount_description: '',
                    discount_percentage: '',
                  },
                ],
              };
            }

            return discount;
          },
        );
    },

    removeRatingInformationDiscountsDiscount: (
      state,
      action: PayloadAction<{
        index: number;
        discountIndex: number;
      }>,
    ) => {
      state.data.rating_information_discounts =
        state.data.rating_information_discounts.map(
          (discount, index: number) => {
            if (index === action.payload.index) {
              return {
                ...discount,
                discounts: discount.discounts.filter(
                  (_, discountIndex: number) =>
                    discountIndex !== action.payload.discountIndex,
                ),
              };
            }

            return discount;
          },
        );
    },

    changeUnlistedDriver: (
      state,
      action: PayloadAction<{
        name: string;
        value: string | boolean;
        index: number;
      }>,
    ) => {
      state.data.unlisted_drivers = state.data.unlisted_drivers.map(
        (driver, index: number) => {
          if (index === action.payload.index) {
            return {
              ...driver,
              [action.payload.name]: action.payload.value,
            };
          }

          return driver;
        },
      );
    },

    changeInsuranceCoverageLiability: (
      state,
      action: PayloadAction<{
        index: number;
        name: 'bodily_injury' | 'property_damage';
        limit?: number;
        premium?: number;
      }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              liability: {
                ...coverage.liability,
                [action.payload.name]: {
                  ...coverage.liability[action.payload.name],
                  ...(action.payload.limit !== undefined && {
                    limit: action.payload.limit,
                  }),
                  ...(action.payload.premium !== undefined && {
                    premium: action.payload.premium,
                  }),
                },
              },
            };
          }

          return coverage;
        },
      );
    },

    changeInsuranceCovergaeAccidentBenefits: (
      state,
      action: PayloadAction<{ index: number; value: string }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              accident_benefits: {
                ...coverage.accident_benefits,
                premium: action.payload.value,
              },
            };
          }

          return coverage;
        },
      );
    },

    changeInsuranceCoverageUninsuredAutomobile: (
      state,
      action: PayloadAction<{ index: number; value: string }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              uninsured_automobile: {
                ...coverage.uninsured_automobile,
                premium: action.payload.value,
              },
            };
          }

          return coverage;
        },
      );
    },

    changeInsuranceCoverageDirectCompensation: (
      state,
      action: PayloadAction<{
        index: number;
        deductible?: string;
        premium?: string;
      }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              direct_compensation: {
                ...coverage.direct_compensation,
                ...(action.payload.deductible !== undefined && {
                  deductible: action.payload.deductible,
                }),
                ...(action.payload.premium !== undefined && {
                  premium: action.payload.premium,
                }),
              },
            };
          }

          return coverage;
        },
      );
    },

    changeInsuranceCoverageLossOrDamage: (
      state,
      action: PayloadAction<{
        index: number;
        name:
          | 'all_perils'
          | 'collision_or_upset'
          | 'comprehensive'
          | 'specified_perils';
        deductible: number;
        premium: number;
      }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              loss_or_damage: {
                ...coverage.loss_or_damage,
                [action.payload.name]: {
                  ...coverage.loss_or_damage[action.payload.name],
                  ...(action.payload.deductible !== undefined && {
                    deductible: action.payload.deductible,
                  }),
                  ...(action.payload.premium !== undefined && {
                    premium: action.payload.premium,
                  }),
                },
              },
            };
          }

          return coverage;
        },
      );
    },

    changePolicyChangeFormsFamilyProtectionCoverage: (
      state,
      action: PayloadAction<{ index: number; value: string }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              policy_change_forms: {
                ...coverage.policy_change_forms,
                family_protection_coverage: {
                  ...coverage.policy_change_forms.family_protection_coverage,
                  premium: action.payload.value,
                },
              },
            };
          }

          return coverage;
        },
      );
    },

    changePolicyChangeFormsOtherCoverages: (
      state,
      action: PayloadAction<{
        index: number;
        subIndex: number;
        name: string;
        value: string;
      }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              policy_change_forms: {
                ...coverage.policy_change_forms,
                other_coverages:
                  coverage.policy_change_forms.other_coverages.map(
                    (otherCoverage, otherCoverageIndex: number) => {
                      if (action.payload.subIndex === otherCoverageIndex) {
                        return {
                          ...otherCoverage,
                          [action.payload.name]: action.payload.value,
                        };
                      }

                      return otherCoverage;
                    },
                  ),
              },
            };
          }

          return coverage;
        },
      );
    },

    addPolicyChangeFormsOtherCoverages: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload) {
            return {
              ...coverage,
              policy_change_forms: {
                ...coverage.policy_change_forms,
                other_coverages: [
                  ...coverage.policy_change_forms.other_coverages,
                  {
                    name: '',
                    deductible_limit: '',
                    premium: '',
                  },
                ],
              },
            };
          }

          return coverage;
        },
      );
    },

    removePolicyChangeFormsOtherCoverages: (
      state,
      action: PayloadAction<{ index: number; subIndex: number }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              policy_change_forms: {
                ...coverage.policy_change_forms,
                other_coverages:
                  coverage.policy_change_forms.other_coverages.filter(
                    (_, subIndex: number) =>
                      subIndex !== action.payload.subIndex,
                  ),
              },
            };
          }

          return coverage;
        },
      );
    },

    changeDeclarationOfApplicant: (
      state,
      action: PayloadAction<{ name: string; value: string }>,
    ) => {
      state.data.declaration_of_applicant = {
        ...state.data.declaration_of_applicant,
        [action.payload.name]: action.payload.value,
      };
    },

    changeReportOfBrokerAgent: (
      state,
      action: PayloadAction<{ name: string; value: string | boolean }>,
    ) => {
      state.data.report_of_broker_agent = {
        ...state.data.report_of_broker_agent,
        [action.payload.name]: action.payload.value,
      };
    },

    changeOtherLoss: (
      state,
      action: PayloadAction<{
        name: string;
        index: number;
        value: string;
      }>,
    ) => {
      state.data.other_losses = state.data.other_losses.map(
        (claim, index: number) => {
          if (index === action.payload.index) {
            return {
              ...claim,
              [action.payload.name]: action.payload.value,
            };
          }

          return claim;
        },
      );
    },

    addOtherLoss: (state) => {
      state.data.other_losses = [
        ...state.data.other_losses,
        {
          auto_nr: '',
          date: '',
          details: '',
          driver_nr: null,
          is_ab: false,
          is_bi: false,
          is_coll_ap: false,
          is_comp_sp: false,
          is_dcpd: false,
          is_pd: false,
          is_ua: false,
          paid: '',
        },
      ];
    },

    removeOtherLoss: (state, action: PayloadAction<number>) => {
      state.data.other_losses = state.data.other_losses.filter(
        (_, index: number) => index !== action.payload,
      );
    },

    addMissingMandatoryField: (
      state,
      action: PayloadAction<{
        name: string;
        section: number;
      }>,
    ) => {
      const fieldExists = state.missingMandatoryFields.some(
        (field) => field.name === action.payload.name,
      );

      if (!fieldExists) {
        state.missingMandatoryFields.push({
          name: action.payload.name,
          section: action.payload.section,
        });
        state.isDataSaved = false;
      }
    },

    removeMissingMandatoryField: (state, action: PayloadAction<string>) => {
      const fieldExists = state.missingMandatoryFields.some(
        (field) => field.name === action.payload,
      );

      if (fieldExists) {
        state.missingMandatoryFields = state.missingMandatoryFields.filter(
          (field) => field.name !== action.payload,
        );
        state.isDataSaved = false;
      }
    },

    changeInsuranceCoverageIncomeReplacement: (
      state,
      action: PayloadAction<{
        index: number;
        limit?: string;
        premium?: string;
      }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              optional_increased_accident_benefits: {
                ...coverage.optional_increased_accident_benefits,
                income_replacement: {
                  ...coverage.optional_increased_accident_benefits
                    .income_replacement,
                  ...(action.payload.limit !== undefined && {
                    limit: action.payload.limit,
                  }),
                  ...(action.payload.premium !== undefined && {
                    premium: action.payload.premium,
                  }),
                },
              },
            };
          }

          return coverage;
        },
      );
    },

    changeInsuranceCoverageMedicalRehabilitation: (
      state,
      action: PayloadAction<{
        index: number;
        limit?: string;
        premium?: string;
      }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              optional_increased_accident_benefits: {
                ...coverage.optional_increased_accident_benefits,
                medical_rehabilitation: {
                  ...coverage.optional_increased_accident_benefits
                    .medical_rehabilitation,
                  ...(action.payload.limit !== undefined && {
                    limit: action.payload.limit,
                  }),
                  ...(action.payload.premium !== undefined && {
                    premium: action.payload.premium,
                  }),
                },
              },
            };
          }

          return coverage;
        },
      );
    },

    changeInsuranceCoverageOptionalIncreasedAccidentBenefits: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        value: number;
      }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            return {
              ...coverage,
              optional_increased_accident_benefits: {
                ...coverage.optional_increased_accident_benefits,
                [action.payload.name]: action.payload.value,
              },
            };
          }

          return coverage;
        },
      );
    },

    changeAbReformOptionalIncreasedAccidentBenefits: (
      state,
      action: PayloadAction<{
        index: number;
        name: string;
        limit?: string;
        premium?: string;
      }>,
    ) => {
      state.data.insurance_coverages = state.data.insurance_coverages.map(
        (coverage, index: number) => {
          if (index === action.payload.index) {
            const currentValue =
              coverage.ab_reform_optional_increased_accident_benefits[
                action.payload
                  .name as keyof typeof coverage.ab_reform_optional_increased_accident_benefits
              ];
            return {
              ...coverage,
              ab_reform_optional_increased_accident_benefits: {
                ...coverage.ab_reform_optional_increased_accident_benefits,
                [action.payload.name]: {
                  ...currentValue,
                  ...(action.payload.limit !== undefined && {
                    limit: action.payload.limit,
                  }),
                  ...(action.payload.premium !== undefined && {
                    premium: action.payload.premium,
                  }),
                },
              },
            };
          }

          return coverage;
        },
      );
    },

    changeBrokerAcknowledgement: (state, action: PayloadAction<boolean>) => {
      state.data.has_broker_acknowledged = action.payload;
    },

    addRemark: (state, action: PayloadAction<APIRemark>) => {
      if (
        !state.remarks.some(
          (remark) =>
            remark.validation_key === action.payload.validation_key &&
            remark.item_key === action.payload.item_key,
        )
      ) {
        state.remarks.push(action.payload);
      }
    },

    removeRemark: (
      state,
      action: PayloadAction<{ validation_key: string; item_key: string }>,
    ) => {
      state.remarks = state.remarks.filter(
        (remark) =>
          !(
            remark.validation_key === action.payload.validation_key &&
            remark.item_key === action.payload.item_key
          ),
      );
    },

    updateRemark: (
      state,
      action: PayloadAction<{
        validation_key: string;
        item_key: string;
        data: Partial<Omit<APIRemark, 'validation_key' | 'item_key'>>;
      }>,
    ) => {
      state.remarks = state.remarks.map((remark) => {
        if (
          remark.validation_key === action.payload.validation_key &&
          remark.item_key === action.payload.item_key
        ) {
          return {
            ...remark,
            ...action.payload.data,
          };
        }
        return remark;
      });
    },

    addOrUpdateRemark: (
      state,
      action: PayloadAction<{
        validation_key: string;
        item_key: string;
        data: Partial<Omit<APIRemark, 'validation_key' | 'item_key'>>;
      }>,
    ) => {
      const existingRemarkIndex = state.remarks.findIndex(
        (remark) =>
          remark.validation_key === action.payload.validation_key &&
          remark.item_key === action.payload.item_key,
      );

      if (existingRemarkIndex !== -1) {
        // Update existing remark
        state.remarks[existingRemarkIndex] = {
          ...state.remarks[existingRemarkIndex],
          ...action.payload.data,
        };
      } else {
        // Add new remark with required fields and provided data
        const newRemark: APIRemark = {
          validation_key: action.payload.validation_key,
          item_key: action.payload.item_key,
          status: 'approved',
          text: '',
          text_validator_log_id: null,
          ai_result: '',
          section: null,
          ...action.payload.data, // Override defaults with provided data
        };
        state.remarks.push(newRemark);
      }
    },

    addFile: (state, action: PayloadAction<APIRemarkFile>) => {
      const existing = state.files.find(
        (file) =>
          file.validation_key === action.payload.validation_key &&
          file.item_key === action.payload.item_key,
      );
      if (!existing) {
        state.files.push(action.payload);
        return;
      }
      existing.text = action.payload.text;
      if (action.payload.mandatory && !existing.mandatory) {
        existing.mandatory = true;
        if (existing.is_deferred) {
          existing.is_deferred = false;
          existing.status = 'created';
        }
      }
    },

    removeFile: (
      state,
      action: PayloadAction<{ validation_key: string; item_key: string }>,
    ) => {
      state.files = state.files.filter(
        (file) =>
          !(
            file.validation_key === action.payload.validation_key &&
            file.item_key === action.payload.item_key
          ),
      );
    },

    updateFile: (
      state,
      action: PayloadAction<{
        validation_key: string;
        item_key: string;
        new_file?: {
          name: string;
          file: string;
          id: number;
          created_at: string;
        };
        file_validator_log_id?: number | null;
        status?: APIRemarkStatus;
        ai_result?: string;
      }>,
    ) => {
      state.files = state.files.map((file) => {
        if (
          file.validation_key === action.payload.validation_key &&
          file.item_key === action.payload.item_key
        ) {
          const updatesFile = {
            ...file,
            ...(action.payload.status && { status: action.payload.status }),
            ...(action.payload.file_validator_log_id !== undefined && {
              file_validator_log_id: action.payload.file_validator_log_id,
            }),
            ...(action.payload.ai_result && {
              ai_result: action.payload.ai_result,
            }),
          };

          if (action.payload.new_file) {
            updatesFile.files = [
              ...file.files,
              {
                id: action.payload.new_file.id,
                name: action.payload.new_file.name,
                url: action.payload.new_file.file,
                created_at: action.payload.new_file.created_at,
              },
            ];
          }

          return updatesFile;
        }
        return file;
      });
    },

    setFileDeferredStatus: (
      state,
      action: PayloadAction<{
        validation_key: string;
        item_key: string;
        is_deferred: boolean;
      }>,
    ) => {
      state.files = state.files.map((file) => {
        if (
          file.validation_key === action.payload.validation_key &&
          file.item_key === action.payload.item_key
        ) {
          return {
            ...file,
            is_deferred: action.payload.is_deferred,
            status: action.payload.is_deferred ? 'pending' : 'created',
          };
        }
        return file;
      });
    },

    removeSubFile: (
      state,
      action: PayloadAction<{
        validation_key: string;
        item_key: string;
        id: number;
      }>,
    ) => {
      state.files = state.files.map((file) => {
        if (
          file.validation_key === action.payload.validation_key &&
          file.item_key === action.payload.item_key
        ) {
          const remainingFiles = file.files.filter(
            (f) => f.id !== action.payload.id,
          );
          return {
            ...file,
            files: remainingFiles,
            status: file.is_deferred ? 'pending' : 'created',

            file_validator_log_id: null,
            ai_result: '',
          };
        }
        return file;
      });
    },

    changeBilling: (
      state,
      action: PayloadAction<{ name: string; value: string | boolean }>,
    ) => {
      state.data.billing = {
        ...state.data.billing,
        [action.payload.name]: action.payload.value,
      };

      if (action.payload.name === 'payment_schedule') {
        state.data.billing.payment_date = null;
        state.data.billing.payment_day_of_month = null;
        state.data.billing.payment_method = null;
        state.data.billing.has_down_payment = null;
        state.data.billing.down_payment_method = null;
        state.data.billing.down_payment_amount = null;
        state.data.billing.down_payment_date = null;
      }

      if (!state.data.billing.has_down_payment) {
        state.data.billing.down_payment_method = null;
        state.data.billing.down_payment_amount = null;
        state.data.billing.down_payment_date = null;
      }
    },

    changeMethodOfPayment: (
      state,
      action: PayloadAction<{ name: string; value: string | number | boolean }>,
    ) => {
      state.data.method_of_payment = {
        ...state.data.method_of_payment,
        [action.payload.name]: action.payload.value,
      };
    },

    updatePayments: (state, action: PayloadAction<APIApplicationPayment[]>) => {
      state.payments = action.payload;
    },

    changeAccountHolder: (
      state,
      action: PayloadAction<{
        holder: 'first' | 'second';
        name: string;
        value: string | boolean;
      }>,
    ) => {
      if (action.payload.holder === 'first') {
        state.data.account_holders.first = {
          ...state.data.account_holders.first,
          [action.payload.name]: action.payload.value,
        };

        // If setting is_same_as_applicant to true, sync with applicant data
        if (
          action.payload.name === 'is_same_as_applicant' &&
          action.payload.value === true
        ) {
          const applicant = state.data.applicant;
          const phonePrefix = applicant.phone_home_prefix || '';
          const phoneNumber = applicant.phone_home || '';

          state.data.account_holders.first = {
            ...state.data.account_holders.first,
            email: applicant.email,
            first_name: applicant.first_name,
            middle_name: applicant.middle_name,
            last_name: applicant.last_name,
            address: applicant.address,
            city: applicant.city,
            province_code: applicant.province_code,
            postal_code: applicant.postal_code,
            phone: `${phonePrefix}${phoneNumber}`,
            is_same_as_co_applicant: false,
          };
        }

        if (
          action.payload.name === 'is_same_as_co_applicant' &&
          action.payload.value === true
        ) {
          const coApplicant = state.data.co_applicant;
          const applicant = state.data.applicant;
          const phonePrefix = applicant.phone_home_prefix || '';
          const phoneNumber = applicant.phone_home || '';

          state.data.account_holders.first = {
            ...state.data.account_holders.first,
            email: coApplicant.email,
            first_name: coApplicant.first_name,
            middle_name: coApplicant.middle_name,
            last_name: coApplicant.last_name,
            address: applicant.address,
            city: applicant.city,
            province_code: applicant.province_code,
            postal_code: applicant.postal_code,
            phone: `${phonePrefix}${phoneNumber}`,
            is_same_as_applicant: false,
          };
        }
      } else {
        state.data.account_holders.second = {
          ...state.data.account_holders.second,
          [action.payload.name]: action.payload.value,
        };

        // If setting is_same_as_co_applicant to true, sync with co-applicant data
        if (
          action.payload.name === 'is_same_as_co_applicant' &&
          action.payload.value === true
        ) {
          const coApplicant = state.data.co_applicant;
          state.data.account_holders.second = {
            ...state.data.account_holders.second,
            email: coApplicant.email,
            first_name: coApplicant.first_name,
            middle_name: coApplicant.middle_name,
            last_name: coApplicant.last_name,
          };
        }
      }
    },

    changeAgencyBilling: (
      state,
      action: PayloadAction<{ name: string; value: string | boolean }>,
    ) => {
      if (!state.data) return;

      if (!state.data.agency_billing) {
        state.data.agency_billing = {
          financing_type: null,
          is_signed_contract_attached: false,
          province_code: 'ON',
          business_code: 'AUTO',
          total_premium: '0',
          service_fee: '',
          tax: '0',
        };
      }

      // Type-safe update for agency_billing
      if (action.payload.name === 'financing_type') {
        state.data.agency_billing.financing_type = action.payload.value as
          | 'paid_full'
          | 'premium_financing';
      }

      if (action.payload.name === 'is_signed_contract_attached') {
        state.data.agency_billing.is_signed_contract_attached = action.payload
          .value as boolean;
      }

      if (action.payload.name === 'province_code') {
        state.data.agency_billing.province_code = action.payload
          .value as string;
      }

      if (action.payload.name === 'business_code') {
        state.data.agency_billing.business_code = action.payload
          .value as string;
      }

      if (action.payload.name === 'total_premium') {
        state.data.agency_billing.total_premium = action.payload
          .value as string;
      }
      if (action.payload.name === 'service_fee') {
        state.data.agency_billing.service_fee = action.payload.value as string;
      }
    },

    addAdditionalInsured: (state) => {
      if (!state.data) return;
      if (!state.data.additional_insureds) {
        state.data.additional_insureds = [];
      }
      if (state.data.additional_insureds.length >= 2) return;
      state.data.additional_insureds.push({
        first_name: '',
        middle_name: '',
        last_name: '',
        email: '',
      });
    },

    changeAdditionalInsured: (
      state,
      action: PayloadAction<{ index: number; name: string; value: string }>,
    ) => {
      if (!state.data?.additional_insureds?.[action.payload.index]) return;
      (state.data.additional_insureds[action.payload.index] as any)[
        action.payload.name
      ] = action.payload.value;
    },

    removeAdditionalInsured: (state, action: PayloadAction<number>) => {
      if (!state.data?.additional_insureds) return;
      state.data.additional_insureds.splice(action.payload, 1);
    },

    setBinderPDF: (state, action: PayloadAction<APIBinderPDF>) => {
      state.binder_pdf = action.payload;
    },

    setSigning: (state, action: PayloadAction<APISigning | null>) => {
      state.signing = action.payload;
    },

    changeDisclosureAndAttestation: (
      state,
      action: PayloadAction<{
        name: string;
        value: any;
      }>,
    ) => {
      state.data.disclosure_and_attestation = {
        ...state.data.disclosure_and_attestation,
        [action.payload.name]: action.payload.value,
      };
    },

    updateWipStatus: (state, action: PayloadAction<number>) => {
      state.wip_status = action.payload;
    },

    setNotesToUnderwriter: (state, action: PayloadAction<string>) => {
      if (state.data) {
        state.data.notes_to_underwriter = action.payload;
      }
    },
  },
});

export const {
  storeData,
  resetData,
  changePolicyInformation,
  changeLanguage,
  changeCompanyAndBrokerInformation,
  changeApplicant,
  changeCoApplicant,
  changePolicyPeriod,
  changeVehicle,
  changeVehicleAccidentBenefitsQuestion,
  changeVehicleLessor,
  changeVehicleLienholder,
  changeVehicleSummary,
  changeUnlistedVehicles,
  changeDriver,
  changeDriverVehiclePercentageUse,
  changeDriverSummary,
  changeMostRecentPolicy,
  changeUnlistedSpouse,
  changeAccidentsAndClaims,
  addAccidentsAndClaims,
  removeAccidentsAndClaims,
  changeConvictions,
  addConviction,
  removeConviction,
  changeRatingInformationDrivingRecord,
  addRatingInformationDrivingRecord,
  removeRatingInformationDrivingRecord,
  changeRatingInformationDiscounts,
  addRatingInformationDiscounts,
  removeRatingInformationDiscount,
  changeRatingInformationDiscountsDiscount,
  addRatingInformationDiscountsDiscount,
  removeRatingInformationDiscountsDiscount,
  changeUnlistedDriver,
  changeInsuranceCoverageLiability,
  changeInsuranceCovergaeAccidentBenefits,
  changeInsuranceCoverageUninsuredAutomobile,
  changeInsuranceCoverageDirectCompensation,
  changeInsuranceCoverageLossOrDamage,
  changePolicyChangeFormsFamilyProtectionCoverage,
  changePolicyChangeFormsOtherCoverages,
  addPolicyChangeFormsOtherCoverages,
  removePolicyChangeFormsOtherCoverages,
  changeDeclarationOfApplicant,
  changeReportOfBrokerAgent,
  changeIsDataSaved,
  changeOtherLoss,
  addOtherLoss,
  removeOtherLoss,
  addMissingMandatoryField,
  removeMissingMandatoryField,
  changeInsuranceCoverageIncomeReplacement,
  changeInsuranceCoverageMedicalRehabilitation,
  changeInsuranceCoverageOptionalIncreasedAccidentBenefits,
  changeAbReformOptionalIncreasedAccidentBenefits,
  changeBrokerAcknowledgement,
  addRemark,
  removeRemark,
  updateRemark,
  addOrUpdateRemark,
  addFile,
  removeFile,
  updateFile,
  setFileDeferredStatus,
  removeSubFile,
  changeBilling,
  changeMethodOfPayment,
  updatePayments,
  changeAccountHolder,
  changeAgencyBilling,
  addAdditionalInsured,
  changeAdditionalInsured,
  removeAdditionalInsured,
  setBinderPDF,
  setSigning,
  changeDisclosureAndAttestation,
  updateWipStatus,
  setNotesToUnderwriter,
} = autoPersonalFormSlice.actions;

export default autoPersonalFormSlice.reducer;
